import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from "react-redux";
import { Box, Button, TextField, Typography, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Snackbar } from '@mui/material';
import UploadFileBtn from "../Helper/UploadFileBtn/UploadFileBtn";
import SignaturePad from 'react-signature-canvas';
import PackingListService, { fetchAndStorePackingList, downloadLogo, downloadSignature } from "../../Services/http/PackingListService";
import "./PackingListForm.scss";
import { AppDispatch, RootState } from '../../Redux/store';

const PackingListForm = () => {
    const dispatch = useDispatch<AppDispatch>();
    const [name, setName] = useState<string>('');
    const [logo, setLogo] = useState<File | null>(null);
    const [logoPreview, setLogoPreview] = useState<string | null>(null);
    const [signature, setSignature] = useState<string | null>(null);
    const [editingSignature, setEditingSignature] = useState(false);
    const [openModal, setOpenModal] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null); 

    const sigPad = useRef<SignaturePad | null>(null);

    // Fetching data from Redux store
const defaultAccount = useSelector((state: any) => state.account.default_account) as { id?: string };
    const packingList = useSelector((state: RootState) => state.packingList);

    useEffect(() => {
        if (defaultAccount?.id) {
            dispatch(fetchAndStorePackingList(defaultAccount.id));
        }
    }, [dispatch, defaultAccount?.id]);

    useEffect(() => {
        if (packingList) {
            setName(packingList.name || '');
            if (packingList.logo_url) {
                downloadLogo(packingList.logo_url).then((base64Logo) => {
                    if (base64Logo) setLogoPreview(base64Logo);
                });
            }
            if (packingList.signature_url) {
                downloadSignature(packingList.signature_url).then((base64Signature) => {
                    if (base64Signature) setSignature(base64Signature);
                });
            }
        }
    }, [packingList]);

    const handleLogoUpload = (file: File) => {
        setLogo(file);
        setLogoPreview(URL.createObjectURL(file));
    };

    const saveSignature = () => {
        if (sigPad.current && !sigPad.current.isEmpty()) {
            setOpenModal(true);
        } else {
            setErrorMessage("Please provide a signature.");
        }
    };

    const handleAgree = () => {
        const signatureDataURL = sigPad.current?.getTrimmedCanvas().toDataURL('image/png');
        setSignature(signatureDataURL);
        setEditingSignature(false);
        setOpenModal(false);
    };

    const handleDisagree = () => {
        setOpenModal(false);
    };

    const uploadPackingList = async () => {
        try {
            const response = await PackingListService.savePackingList(
                dispatch,
                defaultAccount.id,
                name,
                logo,
                signature
            );
            if (!response) {
                setErrorMessage('An error occurred while saving the packing list.'); 
            } else {
                if (response.logo_url) {
                    setLogoPreview(response.logo_url);
                }
                if (response.signature_url) {
                    setSignature(response.signature_url);
                }
            }
        } catch (error) {
            setErrorMessage('Error occurred while saving packing list.'); 
        }
    };
    
    const handleEditSignature = () => {
        setEditingSignature(true);
    };

    const handleCloseSnackbar = () => {
        setErrorMessage(null); // Clear error message
    };

    return (
        <Box className="packing-list-form">
            <Typography variant="h4" className="form-title">Create Packing List</Typography>
            
            <TextField
                label="Name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                fullWidth
                margin="normal"
                className="input-field"
            />

            <UploadFileBtn
                id="logo-upload"
                onSubmit={handleLogoUpload}
                multiple={false}
                title="Upload Logo"
                fileType={"image"}
                buttonText="Upload Logo"
                classes="upload-btn"
            />
            
            {logoPreview && (
                <Box className="logo-preview">
                    <img src={logoPreview} alt="Logo Preview" className="logo-image" />
                    <Button
                        variant="outlined"
                        color="secondary"
                        onClick={() => { setLogo(null); setLogoPreview(null); }}
                        className="edit-logo-btn"
                    >
                        Edit Logo
                    </Button>
                </Box>
            )}

            <Box className="signature-section">
                <Typography variant="h6" className="signature-label">Signature</Typography>
                {signature && !editingSignature ? (
                    <Box>
                        <img src={signature} alt="Signature" className="signature-image" />
                        <Button
                            variant="outlined"
                            color="primary"
                            onClick={handleEditSignature}
                            className="edit-signature-btn"
                        >
                            Edit Signature
                        </Button>
                    </Box>
                ) : (
                    <Box className="signature-pad-container">
                        <SignaturePad ref={sigPad} canvasProps={{ className: "signature-pad" }} />
                        <Button onClick={saveSignature} variant="contained" color="primary" className="save-signature-btn">
                            Save Signature
                        </Button>
                    </Box>
                )}
            </Box>

            <Button
                variant="contained"
                color="primary"
                onClick={uploadPackingList}
                className="save-btn"
                size="large"
            >
                Save
            </Button>

            <Dialog
                open={openModal}
                onClose={handleDisagree}
                className="legal-modal"
            >
                <DialogTitle>Legal & Privacy Acknowledgment</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Customer acknowledges and agrees as follows:
                        <ul>
                            <li>By electronically signing the packing slip, customer and/or authorized packing slip representative is representing and warranting that the slip is accurate and that the same duplicates the information contained in the original packing slip;</li>
                            <li>Better Refunds will submit the completed and signed packing slip to Amazon, or other third party, as applicable, and will seek reimbursement for the customer and/or authorized packing slip representative on the basis of such packing slip;</li>
                            <li>Better Refunds will store the designated individual's signature for future use with this service by the customer and/or authorized packing slip representative, as well as store the completed packing slips for reference;</li>
                            <li>Customer and/or authorized packing slip representative will use this Packing Slip service at its own risk; and Better Refunds will not review the packing slip generated by this service for completeness, accuracy, internal consistency or otherwise.</li>
                            <li>Better Refunds makes no representations or warranties as to the content, format, accuracy, compliance or sufficiency of the information supplied to generate the packing slip, the packing slip itself or any information included therein for any legal or other purpose;</li>
                            <li>The use of this service is subject to and under the terms and conditions of Better Refunds' Terms of Service, the Privacy Policy, and an Opt-in-Agreement and by using this service Customer agrees to and acknowledges the terms and conditions thereof.</li>
                        </ul>
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDisagree} color="secondary">
                        I disagree
                    </Button>
                    <Button onClick={handleAgree} color="primary" autoFocus>
                        I agree
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={!!errorMessage}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                message={errorMessage}
                action={
                    <Button color="inherit" onClick={handleCloseSnackbar}>
                        Close
                    </Button>
                }
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center',
                }}
                sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }} 
            />
        </Box>
    );
};

export default PackingListForm;
